import { useState } from "react";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { sendWithFile } from "../../common/api/apiShared";
import { useTranslation } from "../../localization/useTranslation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { BestPracticeDocumentTemplateCreateModel } from "../BestPracticeDocumentTemplates.types";

export interface AddBestPracticeDocumentTemplateDialogProps {
    onDialogClose: () => void;
    onSave: () => void;
}

export function useAddBestPracticeDocumentTemplateDialog(props: AddBestPracticeDocumentTemplateDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const [viewModel, setViewModel] = useState<BestPracticeDocumentTemplateCreateModel>({
        name: "",
        fileName: "",
    });
    const [file, setFile] = useState<File>();
    const [showErrors, setShowErrors] = useState(false);
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const createMutation = useDotLegalMutation(create);

    const onOkClick = async () => {
        if (isNullOrWhitespace(viewModel.name) || isNullOrWhitespace(viewModel.fileName)) {
            setShowErrors(true);
            return;
        }

        createMutation.mutateAsync(undefined, {
            onSuccess: (response: any) => {
                if (response.success()) {
                    snackbar.show(translateString("bestPracticeDocumentTemplateCreated"));
                    props.onSave();
                }
                if (response.error()) {
                    snackbar.show(translateString(response.error()[0]), "warning");
                }
            },
        });
    };

    const onNameChange = (name: string) => {
        let tempModel = { ...viewModel };
        tempModel.name = name;
        setViewModel(tempModel);
    };

    const onFileChange = (files: Array<DotLegalFile> | undefined) => {
        const fileName = files?.[0]?.fileName ?? "";
        let tempModel = { ...viewModel };
        tempModel.fileName = fileName;
        setFile(files?.[0]?.file);

        if (isNullOrWhitespace(tempModel.name) && !isNullOrWhitespace(fileName)) {
            tempModel.name = fileName.substring(0, fileName.lastIndexOf("."));
        }

        setViewModel(tempModel);
    };

    return {
        viewModel,
        inProgress: createMutation.isLoading,
        uploadPercentage,
        showErrors,
        onOkClick,
        onNameChange,
        onFileChange,
    };

    function create() {
        return sendWithFile<string>("POST", "/best-practice-document-templates", viewModel, file, trackUploadProgress());
    }
}
